import Alpine from "alpinejs";

type HeaderColor = "blue" | "white";

interface MenuStore {
  isOpen: boolean;
  headerColor: HeaderColor;
  toggleMenu: () => void;
  closeMenu: () => void;
  handleEscape: (event: KeyboardEvent) => void;
}

export const menu = () => {
  const header = document.querySelector("header");
  const body = document.body;
  let scrollPosition = 0;

  const isHeaderColor = (value: string): value is HeaderColor => {
    return value === "blue" || value === "white";
  };

  const getDefaultHeaderColor = (): HeaderColor => {
    if (!(header instanceof HTMLElement)) return "blue";
    const headerColor = header.dataset.color ?? "";
    return isHeaderColor(headerColor) ? headerColor : "blue";
  };

  const setHeaderColor = (color: HeaderColor) => {
    if (!(header instanceof HTMLElement)) return;
    header.dataset.color = color;
    const store = Alpine.store("menu") as MenuStore | undefined;
    if (store) {
      store.headerColor = color;
    }
  };

  const initHeaderColorObserver = () => {
    if (!(header instanceof HTMLElement)) return;

    const sections = Array.from(
      document.querySelectorAll<HTMLElement>("[data-color]")
    ).filter((el) => isHeaderColor(el.dataset.color ?? ""));

    const fallbackColor = getDefaultHeaderColor();
    if (sections.length === 0) {
      setHeaderColor(fallbackColor);
      return;
    }

    const resolveColor = () => {
      const pivot = window.innerHeight * 0.35;
      const currentSection = sections.find((section) => {
        const rect = section.getBoundingClientRect();
        return rect.top <= pivot && rect.bottom > pivot;
      });

      if (!currentSection) {
        setHeaderColor(fallbackColor);
        return;
      }

      const nextColor = currentSection.dataset.color ?? "";
      if (!isHeaderColor(nextColor)) {
        setHeaderColor(fallbackColor);
        return;
      }

      setHeaderColor(nextColor);
    };

    const io = new IntersectionObserver(resolveColor, {
      root: null,
      threshold: [0, 0.25, 0.5, 0.75, 1],
    });

    sections.forEach((section) => io.observe(section));
    window.addEventListener("resize", resolveColor);
    resolveColor();
  };

  const lockScroll = () => {
    scrollPosition = window.scrollY;
    body.style.position = "fixed";
    body.style.top = `-${scrollPosition}px`;
    body.style.width = "100%";
    body.style.left = "0";
    body.style.right = "0";
  };

  const unlockScroll = () => {
    body.style.position = "";
    body.style.top = "";
    body.style.width = "";
    body.style.left = "";
    body.style.right = "";
    window.scrollTo(0, scrollPosition);
  };

  Alpine.store("menu", {
    isOpen: false,
    headerColor: getDefaultHeaderColor(),

    toggleMenu() {
      if (this.isOpen) {
        this.closeMenu();
        return;
      }

      this.isOpen = true;
      lockScroll();
    },
    closeMenu() {
      this.isOpen = false;
      unlockScroll();
    },
    handleEscape(event: KeyboardEvent) {
      if (event.key !== "Escape" || !this.isOpen) return;
      this.closeMenu();
    },
  } as MenuStore);

  const initializedStore = Alpine.store("menu") as MenuStore;
  setHeaderColor(initializedStore.headerColor);
  initHeaderColorObserver();
};
